import React, {
  ReactNode, useLayoutEffect, useRef, useState,
} from "react"
import { Box } from "@chakra-ui/react"
import {
  PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH,
  PASS_BUILDER_PREVIEW_PASS_SCALE,
} from "./passBuilderTokens"

type Props = {
  children: ReactNode
  /** When set, uses fixed scaled height (front pass). Omit to measure content (back pass). */
  baseHeight?: number
  /** Multiplied with {@link PASS_BUILDER_PREVIEW_PASS_SCALE} (e.g. back thumbnail). */
  scaleMultiplier?: number
}

/**
 * Scales a 320px-wide pass preview to Sketch sidebar size (377px) in Pass Builder only.
 */
const PassBuilderPreviewScale = ({ children, baseHeight, scaleMultiplier = 1 }: Props) => {
  const innerRef = useRef<HTMLDivElement>(null)
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (baseHeight != null) return undefined

    const element = innerRef.current
    if (!element) return undefined

    const updateHeight = () => setMeasuredHeight(element.offsetHeight)
    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(element)

    return () => observer.disconnect()
  }, [baseHeight, children])

  const unscaledHeight = baseHeight ?? measuredHeight ?? 0
  const scale = PASS_BUILDER_PREVIEW_PASS_SCALE * scaleMultiplier
  const scaledWidth = PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH * scale
  const scaledHeight = unscaledHeight * scale

  return (
    <Box
      w={`${scaledWidth}px`}
      h={unscaledHeight > 0 ? `${scaledHeight}px` : undefined}
      mx="auto"
      position="relative"
      flexShrink={0}
    >
      <Box
        ref={innerRef}
        position="absolute"
        top={0}
        left={0}
        w={`${PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH}px`}
        h={baseHeight != null ? `${baseHeight}px` : undefined}
        transform={`scale(${scale})`}
        transformOrigin="top left"
      >
        {children}
      </Box>
    </Box>
  )
}

export default PassBuilderPreviewScale
